import i18n from "@/core/i18n";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useEffect, useRef } from "react";
import { useLazyGetRelaySessionTokenQuery } from "../services/deviceApi";
import {
  relaySessionEstablished,
  relaySessionFailed,
  relaySessionStarted,
  selectRelayReconnectNonce,
  selectRelaySessionReady,
} from "../services/relaySlice";

/** Backoff schedule for a failed handshake, in ms. Repeats the last entry. */
const RETRY_DELAYS_MS = [1_000, 2_000, 5_000, 15_000, 30_000];

/** Re-mint this far into the token's life, so no request straddles the gap. */
const REFRESH_FRACTION = 0.8;
const MIN_REFRESH_MS = 30_000;

const retryDelay = (attempt: number): number =>
  RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)]!;

/**
 * Establishes and maintains the relay-origin cookie for the current session.
 *
 * A cookie rather than a bearer header because three transports cannot carry
 * one: event pictures and the dashboard are `<img>`/`<iframe>` elements, and
 * detections arrive over a `WebSocket`. A cookie is the only credential the
 * browser attaches to all of them.
 *
 * Maintained, not merely established. The token carries an `expiresIn` and the
 * cookie dies with it, so this re-mints ahead of expiry; a failure retries with
 * backoff rather than being terminal. It used to run exactly once and discard
 * `expiresIn`, so a single blip — or simply a long session — left every
 * relay-backed project unreachable until a page reload, with nothing on screen
 * saying why.
 *
 * Re-runs whenever the organization changes, because the cookie is scoped to
 * one organization and a stale one would authorize against the wrong fleet.
 */
export const useRelaySession = (organizationId: number | null | undefined) => {
  const dispatch = useAppDispatch();
  const ready = useAppSelector(selectRelaySessionReady);
  const reconnectNonce = useAppSelector(selectRelayReconnectNonce);
  const [fetchToken] = useLazyGetRelaySessionTokenQuery();

  // Held in a ref so a retry chain keeps its place across its own scheduled
  // callbacks; reset whenever the effect re-runs, which is what a manual retry,
  // a remount and an organization switch all are.
  const attemptRef = useRef(0);

  useEffect(() => {
    if (!organizationId) {
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    attemptRef.current = 0;

    const schedule = (delay: number) => {
      timer = setTimeout(() => {
        if (!cancelled) {
          void establish();
        }
      }, delay);
    };

    // Messages come from `i18n.t`, not a hook's `t`: as an effect dependency
    // it would restart the handshake on every language switch.
    const fail = (message: string) => {
      dispatch(relaySessionFailed(message));
      schedule(retryDelay(attemptRef.current++));
    };

    const establish = async () => {
      try {
        const { token, relayUrl, expiresIn } = await fetchToken().unwrap();

        // credentials: "include" is mandatory — this request's only purpose is
        // to have the relay set a cookie on its own origin.
        const response = await fetch(`${relayUrl}/session`, {
          method: "POST",
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cancelled) {
          return;
        }

        if (!response.ok) {
          fail(i18n.t("device:relay.rejected", { status: response.status }));
          return;
        }

        dispatch(relaySessionEstablished(relayUrl));
        attemptRef.current = 0;
        schedule(Math.max(expiresIn * 1000 * REFRESH_FRACTION, MIN_REFRESH_MS));
      } catch {
        if (!cancelled) {
          // A deployment with no relay configured answers 503 here. Retrying is
          // still right: it costs one request per backoff step, and every
          // project reachable over the LAN keeps working regardless.
          fail(i18n.t("device:relay.unavailable"));
        }
      }
    };

    // Synchronously, before the first await — see `relaySessionStarted`.
    dispatch(relaySessionStarted());
    void establish();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dispatch, fetchToken, organizationId, reconnectNonce]);

  return ready;
};
